import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { Filters, Location } from "../types";

type Mode = "day" | "week" | "month" | "quarter" | "year" | "custom";
const iso = (d: Date) => d.toISOString().slice(0, 10);
const nowYM = () => new Date().toISOString().slice(0, 7);

function monthRange(ym: string) {
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, "0")}` };
}
function shiftYM(ym: string, delta: number) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

// --- Week helpers (calendar weeks, Monday–Sunday) ---
function mondayOf(d: Date): string {
  const u = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = (u.getUTCDay() + 6) % 7; // 0 = Monday … 6 = Sunday
  u.setUTCDate(u.getUTCDate() - dow);
  return iso(u);
}
function addDaysIso(isoStr: string, n: number): string {
  const [y, m, d] = isoStr.split("-").map(Number);
  return iso(new Date(Date.UTC(y, m - 1, d + n)));
}
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtMD(isoStr: string): string {
  const [, m, d] = isoStr.split("-").map(Number);
  return `${MON[m - 1]} ${d}`;
}
function weekLabel(mondayIso: string): string {
  return `${fmtMD(mondayIso)} – ${fmtMD(addDaysIso(mondayIso, 6))}`;
}
// Every calendar week (by its Monday) that overlaps the given YYYY-MM.
function weeksOfMonth(ym: string): string[] {
  const [y, m] = ym.split("-").map(Number);
  const lastOfMonth = new Date(Date.UTC(y, m, 0));
  let cursor = mondayOf(new Date(Date.UTC(y, m - 1, 1)));
  const weeks: string[] = [];
  while (new Date(`${cursor}T00:00:00Z`) <= lastOfMonth) {
    weeks.push(cursor);
    cursor = addDaysIso(cursor, 7);
  }
  return weeks;
}

/**
 * Shared period/location/department filter bar (Day/Week/Month/Quarter/Year/Custom
 * with month & year navigation). Emits resolved Filters via onChange.
 */
export function PeriodFilters({
  locations, onChange, initialMode = "month", showSearch = false, trailing, footer, storageKey,
}: {
  locations: Location[];
  onChange: (f: Filters) => void;
  initialMode?: Mode;
  showSearch?: boolean;
  trailing?: ReactNode;
  footer?: ReactNode;
  storageKey?: string;   // when set, the selection is remembered per browser
}) {
  // Restore the last-used selection (per browser) so returning to the page
  // keeps the chosen period/location instead of resetting.
  const saved: any = (() => {
    if (!storageKey) return null;
    try { return JSON.parse(localStorage.getItem(`pf:${storageKey}`) || "null"); }
    catch { return null; }
  })();

  const [location, setLocation] = useState<string>(saved?.location ?? "");
  const [department, setDepartment] = useState<string>(saved?.department ?? "");
  const [search, setSearch] = useState<string>(saved?.search ?? "");
  const [mode, setMode] = useState<Mode>(saved?.mode ?? initialMode);
  const [month, setMonth] = useState<string>(saved?.month ?? nowYM());
  const [weekOf, setWeekOf] = useState<string>(saved?.weekOf ?? mondayOf(new Date()));
  const [year, setYear] = useState<number>(saved?.year ?? new Date().getFullYear());
  const [from, setFrom] = useState<string>(saved?.from ?? "");
  const [to, setTo] = useState<string>(saved?.to ?? "");

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(`pf:${storageKey}`,
        JSON.stringify({ location, department, search, mode, month, weekOf, year, from, to }));
    } catch { /* storage unavailable */ }
  }, [storageKey, location, department, search, mode, month, weekOf, year, from, to]);

  const range = useMemo(() => {
    const end = iso(new Date());
    if (mode === "month") return monthRange(month);
    if (mode === "year") return { from: `${year}-01-01`, to: `${year}-12-31` };
    if (mode === "custom") return { from: from || undefined, to: to || undefined };
    if (mode === "day") return { from: end, to: end };
    if (mode === "week") return { from: weekOf, to: addDaysIso(weekOf, 6) };
    const q0 = new Date(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) * 3, 1);
    return { from: iso(q0), to: end };
  }, [mode, month, weekOf, year, from, to]);

  // Weeks of the month that the selected week belongs to — lets you browse the
  // entire month a week at a time (and the arrows step past the month edges).
  const weekOptions = useMemo(() => {
    const ym = weekOf.slice(0, 7);
    const list = weeksOfMonth(ym);
    if (!list.includes(weekOf)) list.push(weekOf);
    return list.sort().map((w) => ({ value: w, label: weekLabel(w) }));
  }, [weekOf]);

  useEffect(() => {
    onChange({
      location: location || undefined, department: department || undefined,
      search: search || undefined, from: range.from, to: range.to,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, department, search, range.from, range.to]);

  const reset = () => {
    setLocation(""); setDepartment(""); setSearch(""); setMode(initialMode);
    setMonth(nowYM()); setWeekOf(mondayOf(new Date())); setYear(new Date().getFullYear());
    setFrom(""); setTo("");
  };

  const modes: Mode[] = ["day", "week", "month", "quarter", "year", "custom"];

  return (
    <div className="panel">
      <div className="row" style={{ marginBottom: 12 }}>
        {modes.map((m) => (
          <span key={m} className={`chip ${mode === m ? "active" : ""}`}
                onClick={() => setMode(m)} style={{ textTransform: "capitalize" }}>{m}</span>
        ))}
      </div>
      <div className="filters">
        <div className="field">
          <label>Location</label>
          <select value={location} onChange={(e) => setLocation(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </div>
        {mode === "week" && (
          <div className="field">
            <label>Week ({MON[Number(weekOf.slice(5, 7)) - 1]})</label>
            <div className="row">
              <button className="ghost" onClick={() => setWeekOf(addDaysIso(weekOf, -7))}>‹</button>
              <select value={weekOf} onChange={(e) => setWeekOf(e.target.value)} style={{ minWidth: 150 }}>
                {weekOptions.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
              <button className="ghost" onClick={() => setWeekOf(addDaysIso(weekOf, 7))}>›</button>
            </div>
          </div>
        )}
        {mode === "month" && (
          <div className="field">
            <label>Month</label>
            <div className="row">
              <button className="ghost" onClick={() => setMonth(shiftYM(month, -1))}>‹</button>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
              <button className="ghost" onClick={() => setMonth(shiftYM(month, 1))}>›</button>
            </div>
          </div>
        )}
        {mode === "year" && (
          <div className="field">
            <label>Year</label>
            <div className="row">
              <button className="ghost" onClick={() => setYear(year - 1)}>‹</button>
              <input type="number" value={year} style={{ width: 90 }}
                     onChange={(e) => setYear(Number(e.target.value))} />
              <button className="ghost" onClick={() => setYear(year + 1)}>›</button>
            </div>
          </div>
        )}
        {mode === "custom" && (<>
          <div className="field"><label>From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div className="field"><label>To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        </>)}
        <div className="field">
          <label>Department</label>
          <input placeholder="e.g. Employees" value={department}
                 onChange={(e) => setDepartment(e.target.value)} />
        </div>
        {showSearch && (
          <div className="field">
            <label>Search (name or ID)</label>
            <input placeholder="e.g. 00000T23 or Mayoori" value={search}
                   onChange={(e) => setSearch(e.target.value)} />
          </div>
        )}
        <button className="ghost" onClick={reset}>Reset</button>
        {trailing}
      </div>
      <div className="row" style={{ justifyContent: "space-between", marginTop: 4 }}>
        <div className="notice">Showing {range.from ?? "—"} → {range.to ?? "—"}
          {location ? ` · ${location}` : " · all locations"}</div>
        {footer}
      </div>
    </div>
  );
}
